/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Login page
 */

import { useState, useEffect } from 'react'
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Link from '@mui/material/Link'
import Alert from '@mui/material/Alert'
import Divider from '@mui/material/Divider'
import { Car } from 'lucide-react'
import GoogleButton from '../../components/auth/GoogleButton'
import { useAuth } from '../../contexts/AuthContext'
import { loginPageStyles } from './LoginPage.styles'

function LoginPage() {
    const { t } = useTranslation()
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const { login, loginWithGoogle, isAuthenticated } = useAuth()
    const navigate = useNavigate()
    const location = useLocation()
    const from = (location.state as any)?.from?.pathname || '/app'

    useEffect(() => {
        if (isAuthenticated) {
            navigate(from, { replace: true })
        }
    }, [isAuthenticated, from, navigate])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')
        setLoading(true)

        try {
            await login(email, password)
            // navigation is triggered via useEffect once isAuthenticated=true
        } catch (err) {
            setError(t('auth.login.invalidCredentials'))
        } finally {
            setLoading(false)
        }
    }

    const handleGoogleLogin = async () => {
        setError('')
        setLoading(true)

        try {
            await loginWithGoogle()
            // navigation is triggered via useEffect once isAuthenticated=true
        } catch (err) {
            setError(t('auth.googleLoginFailed'))
        } finally {
            setLoading(false)
        }
    }

    return (
        <Container maxWidth="xs" sx={loginPageStyles.container}>
            <Box sx={loginPageStyles.logoWrapper}>
                <Box sx={loginPageStyles.logoIcon}>
                    <Car size={28} color="white" />
                </Box>
                <Typography variant="h5" fontWeight={700} textAlign="center">
                    {t('auth.login.title')}
                </Typography>
            </Box>

            <Paper elevation={0} sx={loginPageStyles.paper}>
                {error && (
                    <Alert severity="error" sx={loginPageStyles.errorAlert}>
                        {error}
                    </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit} sx={loginPageStyles.form}>
                    <TextField
                        fullWidth
                        label={t('common.email')}
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        autoComplete="email"
                    />
                    <TextField
                        fullWidth
                        label={t('common.password')}
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        autoComplete="current-password"
                    />
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        size="large"
                        disabled={loading}
                    >
                        {loading ? t('common.loading') : t('auth.login.button')}
                    </Button>
                </Box>

                <Divider sx={loginPageStyles.divider}>{t('common.or')}</Divider>

                <GoogleButton
                    onClick={handleGoogleLogin}
                    disabled={loading}
                    label={t('auth.login.withGoogle')}
                />

                <Typography variant="body2" color="text.secondary" textAlign="center" mt={2}>
                    {t('auth.login.noAccount')}{' '}
                    <Link component={RouterLink} to="/register" fontWeight={600}>
                        {t('auth.login.registerLink')}
                    </Link>
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center" mt={1}>
                    {t('auth.login.forgotPassword')}{' '}
                    <Link component={RouterLink} to="/reset-password" fontWeight={600}>
                        {t('auth.login.forgotPasswordLink')}
                    </Link>
                </Typography>
            </Paper>
        </Container>
    )
}

export default LoginPage
